/**
 * Cheap, offline language detection for pasted story text — enough to pick the
 * story's language among the locales we support, so a Russian text pasted into
 * an English UI doesn't get English prompts (and an English voice) by default.
 *
 * Script first (Cyrillic vs Latin), then diacritics/function words. Returns
 * null when there is too little signal — the caller keeps its current choice.
 */
export type DetectableLanguage = 'ru' | 'uk' | 'en' | 'cs' | 'de' | 'fr';

const MIN_CHARS = 40;

export function detectLanguage(text: string): DetectableLanguage | null {
  const s = text.trim();
  if (s.length < MIN_CHARS) return null;

  const letters = s.replace(/[^\p{L}]/gu, '');
  if (letters.length < MIN_CHARS / 2) return null;
  const cyr = (letters.match(/[Ѐ-ӿ]/g) ?? []).length;
  const lat = (letters.match(/[A-Za-zÀ-ɏ]/g) ?? []).length;

  if (cyr > lat) {
    // Ukrainian-only letters (і, ї, є, ґ) vs Russian-only (ы, э, ъ, ё).
    const uk = (letters.match(/[іїєґІЇЄҐ]/g) ?? []).length;
    const ru = (letters.match(/[ыэъёЫЭЪЁ]/g) ?? []).length;
    return uk > ru ? 'uk' : 'ru';
  }
  if (lat === 0) return null;

  const lower = s.toLowerCase();
  const count = (re: RegExp) => (lower.match(re) ?? []).length;
  const scores: Record<Exclude<DetectableLanguage, 'ru' | 'uk'>, number> = {
    cs: count(/[ěščřžýáíéůúďťň]/g) * 2 + count(/\b(a|je|se|na|že|byl|byla|jsem|ale|jak|tak|když)\b/g),
    de: count(/[äöüß]/g) * 2 + count(/\b(und|der|die|das|ist|nicht|ein|eine|sie|er|mit|auf|war)\b/g),
    fr: count(/[éèêàçùâîôû]/g) + count(/\b(le|la|les|et|est|une|un|des|il|elle|dans|pas|que|avec)\b/g),
    en: count(/\b(the|and|was|is|he|she|they|with|that|of|to|in|it|his|her)\b/g),
  };
  // Czech háček/ring letters are decisive even at low counts (no other
  // supported Latin language has ě/ř/ů).
  if (count(/[ěřů]/g) >= 2) return 'cs';
  const best = (Object.entries(scores) as Array<[DetectableLanguage, number]>).sort((a, b) => b[1] - a[1])[0];
  return best && best[1] >= 3 ? best[0] : null;
}
